import axios from 'axios';
import {
  FeedAutoGenerationReason,
  PersonalFeedSummaryRequest,
  SummarizableFeature,
  ThemeSummaryRequest,
} from '@/types';
import { LayoutVariant } from '@/utils/composables/useLayoutVariant';
import { PageName } from './tracking';

function buildTrackingData(
  docType:
    | PersonalFeedSummaryRequest['doc_type']
    | ThemeSummaryRequest['model_type'],
  pageName: PageName,
  url: string,
  autoGenReason?: FeedAutoGenerationReason,
  feature?: SummarizableFeature,
  layoutVariant?: LayoutVariant,
) {
  const data: Record<string, unknown> = {
    page: {
      name: pageName,
      url: url,
    },
    layout_variant: layoutVariant,
  };

  // メールのときはevent_sourceを記録する
  if (autoGenReason === 'from_mail') {
    data['event_source'] = {
      // メールは記事と特許・論文のみで、関連ニュースは存在しないので以下の2パターンとなる
      name:
        docType === 'article' ? 'latest_news' : 'new_technical_literature_mail',
      feature: feature,
    };
  }
  return data;
}

export async function requestPersonalFeedSummaryGeneration(
  request: PersonalFeedSummaryRequest,
  trackingData: {
    pageName: PageName;
    url: string;
    feature?: SummarizableFeature;
    layoutVariant?: LayoutVariant;
  },
  autoGenReason?: FeedAutoGenerationReason,
): Promise<void> {
  const tracking = buildTrackingData(
    request.doc_type,
    trackingData.pageName,
    trackingData.url,
    autoGenReason,
    trackingData?.feature,
    trackingData?.layoutVariant,
  );

  await axios.post('/feed_summaries/personal_feed', {
    ...request,
    auto_generated: autoGenReason === 'from_top',
    tracking_data: tracking,
  });
}

export async function convertToSummary(uuid: string): Promise<void> {
  await axios.post(`/feed_summaries/personal_feed/${uuid}/survey`);
}

export async function requestThemeSummaryGeneration(
  request: ThemeSummaryRequest,
  trackingData: {
    pageName: PageName;
    url: string;
    feature?: SummarizableFeature;
    layoutVariant?: LayoutVariant;
  },
  autoGenReason?: FeedAutoGenerationReason,
): Promise<void> {
  const { theme_id, ...params } = request;
  const tracking = buildTrackingData(
    request.model_type,
    trackingData.pageName,
    trackingData.url,
    autoGenReason,
    trackingData?.feature,
    trackingData?.layoutVariant,
  );

  await axios.post(`/feed_summaries/theme/${theme_id}`, {
    ...params,
    tracking_data: tracking,
    auto_generated: autoGenReason === 'from_top',
  });
}

export async function convertThemeSummaryToSurvey(
  themeId: number,
  uuid: string,
): Promise<string> {
  const { data } = await axios.post<{ uuid: string }>(
    `/feed_summaries/theme/${themeId}/${uuid}/survey`,
  );
  return data.uuid;
}
